import React from "react";
import { CgWorkAlt } from "react-icons/cg";
import { FaReact, FaDocker, FaAws } from "react-icons/fa";
import {
  SiExpo,
  SiFirebase,
  SiFramer,
  SiGit,
  SiGithub,
  SiHtml5,
  SiJavascript,
  SiMongodb,
  SiNextdotjs,
  SiPython,
  SiReact,
  SiTailwindcss,
  SiTypescript,
  SiUnrealengine,
  SiPytorch,
  SiTensorflow,
  SiPostgresql,
  SiKeras,
  SiCplusplus,
  SiDocker,
  SiKubernetes,
  SiOnnx,
  SiNvidia,
  SiOpencv,
} from "react-icons/si";
import { LuGraduationCap, LuBriefcase, LuBuilding2 } from "react-icons/lu";
import pidImg from "@/public/polyu-sm.png";
import caperImg from "@/public/instacart-logo.png";
import tiktokImg from "@/public/tiktok-clean.png";
import aifiImg from "@/public/aifi-clean.png";
import polyuImg from "@/public/polyu-sm.png";

const zh = {
  links: [
    {
      name: "首页",
      hash: "#home",
    },
    {
      name: "关于",
      hash: "#about",
    },
    {
      name: "项目",
      hash: "#projects",
    },
    {
      name: "技能",
      hash: "#skills",
    },
    {
      name: "经历",
      hash: "#experience",
    },
    {
      name: "LLMVisualizer",
      hash: "/llmpage",
    },
  ],
  intro: {
    greeting: "你好，我是安王鹏。",
    heading: "全栈 Staff 机器学习工程师",
    main: "我做多模态 LLM 和 VLM 系统，从训练到部署都自己来。这行做了大概十年，时间大多花在让推理足够便宜、足够稳定、能扛住生产规模，而不是花在模型本身。做过的东西跨度挺大，从 CVPR 的研究到每天处理几亿条视频的系统。",
    occupation:
      "现就职于 Instacart（Caper Cart）| 曾任职于 TikTok 与 Meta AI Research（FAIR）| CVPR Spotlight 作者",
    contactButton: "联系我",
    downloadCV: "下载简历",
    statusChip: "正在 Instacart 构建 VLM 系统",
  },
  about: {
    title: "关于我",
    first:
      "我是学控制工程出身的，不是一上来就做机器学习，这个背景到现在还影响着我的思路。那篇 CVPR Spotlight 就是这么来的：把训练深度网络看成一个反馈控制问题，就能用 PID 控制器去驱动优化器（后来扩展到了 IEEE TNNLS，影响因子 11.368）。硕士在清华读的，方向是深度学习优化、姿态估计和人脸识别，相关工作发表在 CVPR、IEEE TNNLS 和 Pattern Recognition，另外还有一项自动结账追踪的美国专利。",
    second:
      "在 Instacart、TikTok、Meta FAIR 和 AiFi 一路做下来，我越来越觉得，决定生产环境里 ML 成不成的，大多是模型周边的工程，而不是模型本身。在 TikTok，用一个多模态 LLM 替掉大约 800 个窄域感知模型，是在 INT8 量化和 Inf2 上的张量并行推理把成本砍掉一半之后才真正跑通的。在 AiFi，我们只用摄像头、不上货架传感器，硬件因此很便宜，但所有压力都压到了检测和追踪上。整条技术栈我都能上手：研究、大规模训练和推理，用 Docker、Kubernetes、TensorRT 做部署和边缘端，再到上面的服务和产品层。我也给 CVPR、ECCV、ICCV 和 IEEE TPAMI 审稿。"
  },
  projects: {
    title: "研究与项目",
    data: [
      {
        title: "深度学习的 PID 控制器",
        description:
            "带动量的 SGD 其实就是个 PI 控制器：用到了当前梯度和累积的历史梯度，但缺了微分项。补上 D 项，优化器就能预判过冲，训练更快也更稳。发表于 CVPR 2018 Spotlight，后来扩展到 IEEE TNNLS（影响因子 11.368）。",
        tags: ["CVPR Spotlight", "Optimization", "PyTorch"],
        imageUrl: pidImg,
        imageFit: "contain" as const,
        imageBg: "oklch(23% 0.025 250)",
        repositoryUrl: "https://openaccess.thecvf.com/content_cvpr_2018/html/An_A_PID_Controller_CVPR_2018_paper.html",
        repoType: "CVPR Page",
        demoType: "Paper",
        demoUrl: "https://arxiv.org/abs/1808.03337",
      },
      {
        title: "Caper Cart 购物助手",
        description:
          "在 Instacart：一个跑在 Caper Cart 购物车上的 VLM，顾客扫描商品时实时确认是哪一件。在 30 万+ SKU 上做到 95% 的 top-1 准确率，我搭的服务和评测基础设施把误识别率降了 30%。购物车对延迟、功耗和成本都有固定预算，所以很大一部分工作是分清哪些跑在端上、哪些放到云端。从模型到产品，我全程负责。",
        tags: ["VLM", "Multimodal", "Edge Inference", "Full Stack"],
        imageUrl: caperImg,
        imageFit: "contain" as const,
        imageBg: "oklch(97% 0.03 145)",
        repositoryUrl: "",
        repoType: "",
        demoType: "Industry",
        demoUrl: "#",
      },
      {
        title: "大规模多模态 LLM",
        description:
          "在 TikTok：用一个多模态 LLM 把视频转成稠密文本，替掉大约 800 个窄域感知模型。替这么多模型，只有当这一个比原来一堆加起来还便宜才划算，所以主要精力都在 INT8 量化和 Inf2 上的张量并行推理上。成本砍掉一半，每天 3 亿条视频的量级才扛得住。后来扩展到了推荐、广告和搜索（AWS re:Invent 2024）。",
        tags: ["LLM", "Multimodal", "Inf2", "re:Invent 2024"],
        imageUrl: tiktokImg,
        imageFit: "contain" as const,
        imageBg: "oklch(12% 0.005 0)",
        repositoryUrl: "https://www.youtube.com/watch?v=cdrZHimJY2k&t=2186",
        repoType: "re:Invent Talk",
        demoType: "AWS Blog",
        demoUrl: "https://aws.amazon.com/cn/blogs/machine-learning/bytedance-processes-billions-of-daily-videos-using-their-multimodal-video-understanding-models-on-aws-inferentia2/",
      },
      {
        title: "自主结账系统",
        description:
          "在 AiFi：只用摄像头的无感结账，类似 Amazon Go，但不用货架重量传感器。只留摄像头，门店造价能压得很低，可视觉系统得把活全干了：顾客和商品的检测、追踪，要在遮挡和人流密集的情况下依然准，才能把账算到对的购物车上。这些算法是我主导的，持有美国专利 US11393213B2。",
        tags: ["Object Detection", "Tracking", "Patent"],
        imageUrl: aifiImg,
        imageFit: "contain" as const,
        imageBg: "oklch(94% 0.04 210)",
        repositoryUrl: "",
        repoType: "",
        demoType: "Patent",
        demoUrl: "https://patents.google.com/patent/US11393213B2",
      },
      {
        title: "PolyU-Face 系统",
        description:
          "在香港理工大学：一条完整的人脸流水线，检测、对齐、识别都包含在内。把整条链路自己搭一遍，会发现论文里常常一带而过的事：前面对齐留下的误差，对最终识别精度的影响比识别模型本身大得多。同期还参与了多人 2D 姿态估计（ICME 2018 口头报告）。",
        tags: ["Face Recognition", "Pose Estimation", "Research"],
        imageUrl: polyuImg,
        imageFit: "contain" as const,
        imageBg: "oklch(18% 0.025 27)",
        repositoryUrl: "",
        repoType: "",
        demoType: "Research",
        demoUrl: "#",
      },
    ],
  },
  skills: {
    title: "我的技能",
    data: [
      // 编程语言
      {
        skill: "Python",
        icon: React.createElement(SiPython),
        color: "#3572A5",
      },
      {
        skill: "C++",
        icon: React.createElement(SiCplusplus),
        color: "#00599C",
      },
      // ML 框架
      {
        skill: "PyTorch",
        icon: React.createElement(SiPytorch),
        color: "#EE4C2C",
      },
      {
        skill: "TensorFlow",
        icon: React.createElement(SiTensorflow),
        color: "#FF6F00",
      },
      {
        skill: "Keras",
        icon: React.createElement(SiKeras),
        color: "#D00000",
      },
      {
        skill: "ONNX",
        icon: React.createElement(SiOnnx),
        color: "#005CED",
      },
      {
        skill: "TensorRT",
        icon: React.createElement(SiNvidia),
        color: "#76B900",
      },
      {
        skill: "OpenCV",
        icon: React.createElement(SiOpencv),
        color: "#5C3EE8",
      },
      // 部署
      {
        skill: "Docker",
        icon: React.createElement(SiDocker),
        color: "#2496ED",
      },
      {
        skill: "Kubernetes",
        icon: React.createElement(SiKubernetes),
        color: "#326CE5",
      },
      // Web 开发
      {
        skill: "TypeScript",
        icon: React.createElement(SiTypescript),
        color: "#3178c6",
      },
      {
        skill: "React",
        icon: React.createElement(SiReact),
        color: "#61dafb",
      },
      {
        skill: "Next.js",
        icon: React.createElement(SiNextdotjs),
        color: "#000000",
      },
      {
        skill: "Tailwind",
        icon: React.createElement(SiTailwindcss),
        color: "#38bdf8",
      },
      // 工具
      {
        skill: "Git",
        icon: React.createElement(SiGit),
        color: "#f05032",
      },
      {
        skill: "PostgreSQL",
        icon: React.createElement(SiPostgresql),
        color: "#336791",
      },
    ],
  },
  experience: {
    title: "我的经历",
    data: [
      {
        title: "Instacart Inc. - 全栈 Staff 机器学习工程师",
        location: "美国加利福尼亚州",
        description:
          "在 Caper Cart 上构建视觉语言模型，顾客扫描商品时实时确认：30 万+ SKU 上 95% 的 top-1 准确率，误识别率下降 30%。模型、推理到购物车产品体验，全程由我负责。",
        icon: React.createElement(LuBriefcase),
        date: "2025 年 4 月 - 至今",
      },
      {
        title: "TikTok Inc. - 高级机器学习工程师",
        location: "美国加利福尼亚州圣何塞",
        description:
          "信任与安全及内容广告：构建了完整的多模态 LLM 系统，把视频转成稠密文本并检测违规内容，每天在 Inf2 上处理 3 亿条视频，效果优于 800 个传统感知模型。把多模态 LLM 扩展到推荐、广告和搜索（在 AWS re:Invent 2024 上做了分享），并通过接入 Whisper 把音频审核指标从 5% 提到了 10%。",
        icon: React.createElement(LuBriefcase),
        date: "2021 年 9 月 - 2025 年 4 月",
      },
      {
        title: "Meta（Facebook）- FAIR 计算机视觉工程师",
        location: "美国加利福尼亚州门洛帕克",
        description:
          "Facebook AI Research：从事可解释 AI 研究以打开计算机视觉模型的「黑箱」，以及面向计算机视觉系统包容性与公平性的负责任 AI 研究。",
        icon: React.createElement(LuBriefcase),
        date: "2020 年 12 月 - 2021 年 8 月",
      },
      {
        title: "AiFi Inc. - 高级研究工程师",
        location: "美国加利福尼亚州圣克拉拉",
        description:
          "主导纯 RGB 摄像头自主结账方案（类似 Amazon Go）的计算机视觉检测算法研发，构建顾客检测追踪与商品检测追踪系统，持有美国专利。",
        icon: React.createElement(LuBriefcase),
        date: "2018 年 6 月 - 2020 年 11 月",
      },
      {
        title: "香港理工大学 - 研究助理",
        location: "中国香港",
        description:
          "研发 PID 优化器加速 CNN 训练（CVPR 2018 Spotlight），构建 PolyU-Face 系统用于人脸检测、对齐与识别。",
        icon: React.createElement(LuBuilding2),
        date: "2016 年 10 月 - 2018 年 4 月",
      },
      {
        title: "清华大学 - 硕士研究生",
        location: "中国北京",
        description:
          "控制工程专业，研究方向为深度学习优化、人体姿态估计、目标检测与人脸识别。担任大数据课程助教。",
        icon: React.createElement(LuGraduationCap),
        date: "2015 年 8 月 - 2018 年 7 月",
      },
      {
        title: "太原钢铁集团 - 控制技术员",
        location: "中国太原",
        description:
          "负责钢铁轧机中 PID 控制器参数调整，将控制理论应用于工业自动化。",
        icon: React.createElement(LuBriefcase),
        date: "2012 年 7 月 - 2014 年 9 月",
      },
      {
        title: "昆明理工大学 - 本科",
        location: "中国昆明",
        description:
          "仪器与测量科学专业，专注于控制理论，掌握 PID 控制系统与信号处理基础。",
        icon: React.createElement(LuGraduationCap),
        date: "2008 年 9 月 - 2012 年 7 月",
      },
    ],
  },
  contact: {
    title: "联系我",
    message:
      "欢迎联系我，尤其是新的工作机会。可以用下面的表单、直接发邮件，或者在 LinkedIn、GitHub 上找我。我会回复的。",
    placeholderEmail: "您的邮箱",
    placeholderMessage: "您的留言",
    buttonSubmit: "发送",
    buttonCopy: "复制邮箱地址",
    toastSent: "邮件发送成功！",
    toastCopied: "邮箱地址已复制到剪贴板！",
  },
  footer: {
    text: "保留所有权利。",
    subtitle: "关于本网站",
    about:
      "使用 React、Next.js、TypeScript、Tailwind CSS、Framer Motion 构建，部署于 Vercel。",
  },
};

export default zh;
